import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, Modal, ModalHandle, TasksStore, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Button, ButtonGroup } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { EmailModal } from './EmailModal';
import { ManageStore } from './ManageStore';
import { PasswordModal } from './PasswordModal';
import { PhoneModal } from './PhoneModal';
import { BsQuestionCircle } from '@react-icons/all-files/bs/BsQuestionCircle';

export interface IUserProfileProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class UserProfile extends React.Component<IUserProfileProps> {
    public async componentDidMount() {
        try {
            await this.manageStore.loadProvidersAsync();
            await this.accountsStore.loadAsync();
        } catch (e: any) {
            this.appStore.alertHandle.setError(e);
        }
    }
    public render() {
        const twoFactorEnabled = this.accountStore.user?.twoFactorEnabled ?? false;

        return (
            <div className="container-fluid px-2 px-md-4">
                <div className="row">
                    <div className="col-md-4 py-2">
                        <h5>User Settings</h5>
                    </div>
                    <div className="col-md-8">
                        {this.accountStore.user && (
                            <div className="container-fluid">
                                <div className="row border-bottom py-2">
                                    <div className="col-md-3 p-0">
                                        <strong>Username</strong>
                                    </div>
                                    <div className="col-md-6 p-0">{this.accountStore.user.userName}</div>
                                </div>

                                <div className="row border-bottom py-2">
                                    <div className="col-md-3 p-0">
                                        <strong>Email</strong>
                                    </div>

                                    <div className="col-md-6 p-0">
                                        {this.accountStore.user.email}
                                        {this.accountStore.user.emailConfirmed && (
                                            <div className="text-success d-inline-block px-2">Verified</div>
                                        )}
                                        {this.accountStore.user.email && !this.accountStore.user.emailConfirmed && (
                                            <div className="d-inline-block px-2">
                                                <a className="text-danger" onClick={this.sendVerifyEmail}>
                                                    {this.sending ? (
                                                        'Sending...'
                                                    ) : (
                                                        <div>
                                                            {!this.emailSent ? 'Send Verification Email' : 'Email Sent'}
                                                        </div>
                                                    )}
                                                </a>

                                                {this.emailSent && (
                                                    <div className="d-inline-block">
                                                        Please Check Your Email and refresh this page
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-3 text-md-right p-0">
                                        {this.accountStore.user.email && (
                                            <a onClick={this.openEmailModal} className="btn btn-link p-0">
                                                Change
                                            </a>
                                        )}
                                        {!this.accountStore.user.email && (
                                            <a onClick={this.openEmailModal} className="text-danger p-0">
                                                Add your email
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="row border-bottom py-2">
                                    <div className="col-md-3 px-0">
                                        <strong>Phone</strong>
                                    </div>
                                    <div className="col-md-6 px-0">
                                        {this.accountStore.user.phoneNumber}
                                        {this.accountStore.user.phoneNumberConfirmed && (
                                            <div className="text-success d-inline-block px-2">Verified</div>
                                        )}
                                    </div>
                                    <div className="col-md-3 text-md-right px-0">
                                        {this.accountStore.user.phoneNumber && (
                                            <a onClick={this.openPhoneModal} className="btn btn-link p-0">
                                                Change
                                            </a>
                                        )}
                                        {!this.accountStore.user.phoneNumber && (
                                            <a onClick={this.openPhoneModal} className="text-danger p-0">
                                                Add your phone number
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="row border-bottom py-2">
                                    <div className="col-md-3 px-0">
                                        <strong>Password</strong>
                                    </div>

                                    <div className="col-md-6 px-0">********</div>

                                    <div className="col-md-3 text-md-right px-0">
                                        <a onClick={this.openChangePasswordModal} className="btn btn-link p-0">
                                            Change
                                        </a>
                                    </div>
                                </div>

                                <div className="row border-bottom py-2">
                                    <div className="col-md-3 p-0">
                                        <strong>Roles</strong>
                                    </div>
                                    <div className="col-md-9 p-0">{this.accountStore.user.roles.join(', ')}</div>
                                </div>

                                <div className="row py-2">
                                    <div className="col-md-3 p-0">
                                        <strong>Accounts</strong>
                                    </div>
                                    <div className="col-md-9 p-0">
                                        {this.accountNames ? this.accountNames.join(', ') : 'n/a'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="col-md-4 py-2 border-top">
                        <h5>Security Settings</h5>
                    </div>

                    <div className="col-md-8 border-top">
                        <div className="container-fluid">
                            <div className="row py-2">
                                <div className="col-md-3 p-0">
                                    <span className="fw-bold">2FA</span>
                                    &nbsp;
                                    <Tooltip message="Two Factor Authentication increases security by using a separate device to secure your account">
                                        <BsQuestionCircle />
                                    </Tooltip>
                                </div>

                                <div className="col-md-6 p-0 fw-bold">
                                    <span className={twoFactorEnabled ? 'text-success' : 'text-danger'}>
                                        {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>

                                <div className="col-md-3 text-md-right px-0">
                                    {!twoFactorEnabled && (
                                        <button onClick={this.enableTwoFactorAsync} className="btn btn-primary mx-2">
                                            Enable
                                        </button>
                                    )}

                                    {twoFactorEnabled && (
                                        <a onClick={this.disableTwoFactorAsync} className="text-danger">
                                            Disable
                                        </a>
                                    )}
                                </div>

                                <div className="col-12 p-0">
                                    {twoFactorEnabled && !this.manageStore.providers?.length && (
                                        <div className="alert-warning">
                                            Although you have Fwo-Factor Authentication enabled, you have no valid
                                            providers to authenticate with. Add your phone number or verify your email
                                            to proceed.
                                        </div>
                                    )}

                                    {!twoFactorEnabled && !!this.manageStore.providers?.length && (
                                        <div className="alert-warning">
                                            You currently don't have Two-Factor Authentication Enabled. We highly
                                            recommend you secure your account with Two-Factor Authentication in case any
                                            of your credentials are compromised.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <EmailModal modalHandle={this.emailModalHandle} />
                <PhoneModal modalHandle={this.phoneModalHandle} />
                <PasswordModal modalHandle={this.passwordModalHandle} />
                <Modal
                    className="modal-sm"
                    title="Confirm"
                    modalHandle={this.disableTwoFactorModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">Disable Two Factor Authentication?</div>
                </Modal>
            </div>
        );
    }

    @bind
    private async enableTwoFactorAsync() {
        if (this.accountStore.user) {
            await this.manageStore.setTwoFactorAsync({
                enabled: true,
            });
        }
    }

    @bind
    private async disableTwoFactorAsync() {
        if (this.accountStore.user) {
            if (await this.disableTwoFactorModalHandle.confirm()) {
                await this.manageStore.setTwoFactorAsync({
                    enabled: false,
                });
            }
        }
    }

    @bind
    private openEmailModal(event: React.MouseEvent<HTMLAnchorElement>) {
        this.emailModalHandle.open();
    }

    @bind
    private openPhoneModal(event: React.MouseEvent<HTMLAnchorElement>) {
        this.phoneModalHandle.open();
    }

    @bind
    private openChangePasswordModal(event: React.MouseEvent<HTMLAnchorElement>) {
        this.passwordModalHandle.open();
    }

    @bind
    @action
    private async sendVerifyEmail(event: React.MouseEvent<HTMLAnchorElement>) {
        this.sending = true;
        const task = this.tasksStore.startSessionTask(`Send Email Verification`);

        try {
            await this.manageStore.sendVerifyEmail();
            this.emailSent = true;
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.sending = false;
        }
    }

    @computed
    private get accountNames() {
        if (this.accountStore.user)
            return this.accountStore.user.accounts
                .map(a => this.accountsStore.accounts.get(a))
                .map(a => a?.accountName ?? 'Other Account');
    }

    @observable
    private emailSent = false;
    @observable
    private sending = false;
    private emailModalHandle = new ModalHandle();
    private phoneModalHandle = new ModalHandle();
    private passwordModalHandle = new ModalHandle();
    private disableTwoFactorModalHandle = new ModalHandle();
    @inject
    private manageStore!: ManageStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
}
